import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild } from '@angular/router';
import { TenantContextService } from '../services/tenant-context.service';
import { environment } from '../../environments/environment';
import { TenantDirectoryService } from '../services/tenant-directory.service';

@Injectable({
  providedIn: 'root',
})
export class TenantResolveGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly tenantContext: TenantContextService,
    private readonly tenantDirectory: TenantDirectoryService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return this.resolveTenant(route);
  }

  async canActivateChild(route: ActivatedRouteSnapshot): Promise<boolean> {
    return this.resolveTenant(route);
  }

  private async resolveTenant(route: ActivatedRouteSnapshot): Promise<boolean> {
    const tenantParam = this.findTenantParam(route);
    if (!tenantParam) {
      return false;
    }

    if (this.tenantContext.getTenantAlias() === tenantParam && this.tenantContext.getTenantId()) {
      return true;
    }

    try {
      const resolved = await this.tenantDirectory.resolveAlias(tenantParam);
      if (resolved?.tenantId) {
        this.tenantContext.setTenantContext(resolved.tenantId, resolved.preferredTenantId ?? tenantParam);
        return true;
      }
    } catch {
      // Ignore alias resolution failures, fall back to the raw tenant param.
    }

    if (environment.tenantAlias && environment.tenantId && tenantParam === environment.tenantAlias) {
      this.tenantContext.setTenantContext(environment.tenantId, tenantParam);
      return true;
    }

    this.tenantContext.setTenantContext(tenantParam, tenantParam);
    return true;
  }

  private findTenantParam(route: ActivatedRouteSnapshot): string | undefined {
    for (const snapshot of route.pathFromRoot) {
      const tenantId = snapshot.paramMap.get('tenantId')?.trim();
      if (tenantId) {
        return tenantId;
      }
    }
    return undefined;
  }
}
